/**
 * Organization Limits Service
 * Handles getting and setting organization limits (admin and user limits)
 */

import { supabase } from './supabase'

/**
 * Get organization limits (checks custom overrides first, then tier defaults)
 */
export const getOrganizationLimits = async (organizationId) => {
  try {
    const { data, error } = await supabase.rpc('get_organization_limits', {
      org_id: organizationId
    })

    if (error) throw error
    return { success: true, data }
  } catch (error) {
    console.error('Error getting organization limits:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Check if organization can add more admins
 */
export const checkCanAddAdmin = async (organizationId) => {
  try {
    const { data, error } = await supabase.rpc('can_add_admin', {
      org_id: organizationId
    })

    if (error) throw error
    return { success: true, data }
  } catch (error) {
    console.error('Error checking admin limit:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Check if organization can add more users
 */
export const checkCanAddUser = async (organizationId) => {
  try {
    const { data, error } = await supabase.rpc('can_add_user', {
      org_id: organizationId
    })

    if (error) throw error
    return { success: true, data }
  } catch (error) {
    console.error('Error checking user limit:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Set custom limits for an organization (Master Admin only)
 * @param {string} organizationId - Organization ID
 * @param {Object} limits - { maxAdmins?: number, maxUsers?: number }
 * @param {boolean} clearCustom - If true, clears custom limits (uses tier defaults)
 */
export const setOrganizationLimits = async (organizationId, limits = {}, clearCustom = false) => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('User not authenticated')
    }

    // Verify user is master admin
    const { data: masterAdminCheck } = await supabase
      .from('master_admins')
      .select('id')
      .eq('user_id', user.id)
      .eq('is_active', true)
      .maybeSingle()

    if (!masterAdminCheck) {
      throw new Error('Only master administrators can set organization limits')
    }

    const updates = {}
    if (clearCustom) {
      updates.custom_max_admins = null
      updates.custom_max_users = null
    } else {
      if (limits.maxAdmins !== undefined) {
        updates.custom_max_admins = limits.maxAdmins === null ? null : parseInt(limits.maxAdmins)
      }
      if (limits.maxUsers !== undefined) {
        updates.custom_max_users = limits.maxUsers === null ? null : parseInt(limits.maxUsers)
      }
    }

    const { data, error } = await supabase
      .from('organizations')
      .update(updates)
      .eq('id', organizationId)
      .select()
      .single()

    if (error) throw error

    return { success: true, data }
  } catch (error) {
    console.error('Error setting organization limits:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get tier limits (default limits for a subscription tier)
 */
export const getTierLimits = async (tierCode) => {
  try {
    const { data, error } = await supabase
      .from('subscription_tiers')
      .select('max_users, max_admins, code, name')
      .eq('code', tierCode)
      .eq('is_active', true)
      .single()

    if (error) throw error
    return { success: true, data }
  } catch (error) {
    console.error('Error getting tier limits:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Set tier limits (Master Admin only)
 * Updates the default limits for a subscription tier
 */
export const setTierLimits = async (tierCode, limits) => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('User not authenticated')
    }

    // Verify user is master admin
    const { data: masterAdminCheck } = await supabase
      .from('master_admins')
      .select('id')
      .eq('user_id', user.id)
      .eq('is_active', true)
      .maybeSingle()

    if (!masterAdminCheck) {
      throw new Error('Only master administrators can set tier limits')
    }

    const updates = {}
    if (limits.maxUsers !== undefined) {
      updates.max_users = limits.maxUsers === null ? null : parseInt(limits.maxUsers)
    }
    if (limits.maxAdmins !== undefined) {
      updates.max_admins = limits.maxAdmins === null ? null : parseInt(limits.maxAdmins)
    }

    const { data, error } = await supabase
      .from('subscription_tiers')
      .update(updates)
      .eq('code', tierCode)
      .select()
      .single()

    if (error) throw error

    return { success: true, data }
  } catch (error) {
    console.error('Error setting tier limits:', error)
    return { success: false, error: error.message }
  }
}

